//=============================================================================
// CUSTOM CART FUNCTIONALITY
//=============================================================================
// This file handles:
// - Loading overlay management
// - Cart operations (add, remove, update)
// - OTP to subscription conversion
// - Quantity management
// - Fetch interception
// - Shipping progress bar
//=============================================================================

(function() {
  'use strict';
  
  //=============================================================================
  // SECTION 1: STATE MANAGEMENT
  //=============================================================================
  // Shared state variables used across all cart functions
  //=============================================================================
  
  // Track initialized selects to prevent duplicate listeners
  const initializedSelects = new WeakSet();
  const initializedButtons = new WeakSet();
  const initializedQuantitySelectors = new WeakSet();

  // Track if overlay is being created to prevent duplicates
  let isCreatingOverlay = false;
  
  // Track cart operation state - shared across all functions
  let isHandlingProductForm = false;
  let pendingAddToCartFetch = null;
  let pendingCartOperations = []; // Track pending cart operations (add, change, update)
  let pendingCartJsCall = null; // Store pending cart.js call to execute after operations complete
  
  //=============================================================================
  // SECTION 2: LOADING OVERLAY MANAGEMENT
  //=============================================================================
  // Functions for creating, showing, and hiding the cart loading overlay
  //=============================================================================
  
  // Create loading overlay for cart
  function createCartLoadingOverlay() {
    // Prevent multiple simultaneous overlay creation
    if (isCreatingOverlay) {
      // Return existing overlay if already being created
      const host = document.querySelector('#cart-drawer') || document.querySelector('cart-drawer');
      if (host && host.shadowRoot) {
        const existingOverlay = host.shadowRoot.getElementById('cart-loading-overlay');
        if (existingOverlay) {
          return existingOverlay;
        }
      }
      const existingOverlay = document.getElementById('cart-loading-overlay');
      if (existingOverlay) {
        return existingOverlay;
      }
    }
    
    isCreatingOverlay = true;
    let overlay = null;
    let container = document.body; // Default container
    
    // Try to find overlay in shadow DOM first
    const host = document.querySelector('#cart-drawer') || document.querySelector('cart-drawer');
    if (host && host.shadowRoot) {
      overlay = host.shadowRoot.getElementById('cart-loading-overlay');
      // Try to find the body part which contains the cart items - this is what we want to cover
      let shadowBody = host.shadowRoot.querySelector('[part="body"]');
      // Use style.setProperty to robustly override styles instead of inlining !important in the string
      // Only apply this if cart is empty
     
      shadowBody.style.setProperty('align-items', 'center', 'important');
      shadowBody.style.setProperty('justify-content', 'center', 'important');
      shadowBody.style.setProperty('height', '100%', 'important');
      if (shadowBody) {
        container = shadowBody;
      } else {
        const shadowContent = host.shadowRoot.querySelector('[part="content"]');
        if (shadowContent) {
          container = shadowContent;
        } else {
          container = host.shadowRoot;
        }
      }
    }
    
    // Fallback to regular DOM if not found in shadow DOM
    if (!overlay) {
      overlay = document.getElementById('cart-loading-overlay');
    }
    
    // Only create new overlay if it doesn't exist
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'cart-loading-overlay';
      overlay.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.98);
        display: none;
        align-items: center;
        justify-content: center;
        z-index: 999999;
        backdrop-filter: blur(2px);
      `;
      overlay.innerHTML = `
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, rgb(243 243 243) 0%, rgb(255 255 255) 50%, rgb(243 243 243) 100%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        "></div>
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          position: relative;
          z-index: 1;
        ">
          <div class="loading-dot" style="
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #DB7061;
            animation: dot-bounce 1.4s ease-in-out infinite;
            animation-delay: 0s;
          "></div>
          <div class="loading-dot" style="
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #DB7061;
            animation: dot-bounce 1.4s ease-in-out infinite;
            animation-delay: 0.2s;
          "></div>
          <div class="loading-dot" style="
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #DB7061;
            animation: dot-bounce 1.4s ease-in-out infinite;
            animation-delay: 0.4s;
          "></div>
        </div>
        <style>
          @keyframes dot-bounce {
            0%, 80%, 100% {
              transform: scale(0.8);
              opacity: 0.5;
            }
            40% {
              transform: scale(1.2);
              opacity: 1;
            }
          }
          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }
        </style>
      `;
      // Ensure container has relative positioning for absolute overlay
      if (container && container !== document.body) {
        const containerStyle = window.getComputedStyle(container);
        if (containerStyle.position === 'static') {
          container.style.position = 'relative';
        }
      }
      container.appendChild(overlay);
    }
    
    isCreatingOverlay = false;
    return overlay;
  }

  // Track if loading is being shown to prevent duplicates
  let isShowingLoading = false;
  
  function showCartLoading() {
    // Prevent multiple simultaneous calls
    if (isShowingLoading) {
      // Just ensure overlay is visible if already showing
      const host = document.querySelector('#cart-drawer') || document.querySelector('cart-drawer');
      
      let overlay = null;
      if (host && host.shadowRoot) {
        overlay = host.shadowRoot.getElementById('cart-loading-overlay');
      }
      if (!overlay) {
        overlay = document.getElementById('cart-loading-overlay');
      }
      if (overlay) {
        overlay.style.display = 'flex';
      }
      // Hide checkout button price
      hideCheckoutButtonPrice();
      // Ensure overflow is hidden
      updateShadowBodyOverflow();
      return;
    }
    
    isShowingLoading = true;
    overlayHidden = false; // Reset flag when showing loading
    isHidingLoading = false; // Reset hiding flag when showing loading
    const overlay = createCartLoadingOverlay();
    overlay.style.display = 'flex';
    
    // Hide checkout button price while loading
    hideCheckoutButtonPrice();
    
    // Ensure overflow is hidden when showing loading
    updateShadowBodyOverflow();
    
    // Reset flag after a short delay
    setTimeout(() => {
      isShowingLoading = false;
    }, 100);
  }
  
  // Hide checkout button price
  function hideCheckoutButtonPrice() {
    const cartDrawer = document.querySelector('#cart-drawer') || document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    
    // Try shadow DOM first
    let priceElement = null;
    if (cartDrawer.shadowRoot) {
      priceElement = cartDrawer.shadowRoot.querySelector('.checkout-button-price');
    }
    
    // Fallback to regular DOM
    if (!priceElement) {
      priceElement = cartDrawer.querySelector('.checkout-button-price');
    }
    
    // Also try finding by button with name="checkout"
    if (!priceElement) {
      const checkoutButton = cartDrawer.shadowRoot?.querySelector('button[name="checkout"]') || 
                             cartDrawer.querySelector('button[name="checkout"]');
      if (checkoutButton) {
        // Find price span in button text
        const buttonText = checkoutButton.textContent || '';
        const priceMatch = buttonText.match(/-\s*(\p{Sc}?\s*[\d,.]+)/u);
        if (priceMatch) {
          // Create a wrapper or use existing span
          priceElement = checkoutButton.querySelector('.checkout-button-price');
          if (!priceElement && checkoutButton.innerHTML.includes(' - ')) {
            // Try to wrap the price if not already wrapped
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = checkoutButton.innerHTML;
            const textNodes = Array.from(tempDiv.childNodes).filter(n => n.nodeType === 3);
            // This is complex, so we'll use CSS instead
          }
        }
      }
    }
    
    if (priceElement) {
      priceElement.style.display = 'none';
    } else {
      // Fallback: add class to cart drawer and use CSS
      cartDrawer.classList.add('cart-loading');
    }
  }
  
  // Show checkout button price
  function showCheckoutButtonPrice() {
    const cartDrawer = document.querySelector('#cart-drawer') || document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    
    // Try shadow DOM first
    let priceElement = null;
    if (cartDrawer.shadowRoot) {
      priceElement = cartDrawer.shadowRoot.querySelector('.checkout-button-price');
    }
    
    // Fallback to regular DOM
    if (!priceElement) {
      priceElement = cartDrawer.querySelector('.checkout-button-price');
    }
    
    if (priceElement) {
      priceElement.style.display = '';
    }
    
    // Remove loading class
    cartDrawer.classList.remove('cart-loading');
  }
  
  // Expose showCartLoading globally so liquid code can call it immediately
  window.showCartLoading = showCartLoading;

  // Manage shadow body overflow based on pending operations or loading state
  function updateShadowBodyOverflow() { 
    const host = document.querySelector('#cart-drawer') || document.querySelector('cart-drawer');
    if (!host || !host.shadowRoot) {
      return;
    }
    
    const shadowBody = host.shadowRoot.querySelector('[part="body"]');
    if (!shadowBody) {
      return;
    }
    
    // Check if loading overlay is visible
    let overlayVisible = false;
    const overlay = host.shadowRoot.getElementById('cart-loading-overlay') || 
                    document.getElementById('cart-loading-overlay');
    if (overlay) {
      const overlayDisplay = overlay.style.display;
      overlayVisible = overlayDisplay !== 'none' && overlayDisplay !== '';
    } else {
    }
    
    // Check if there are actual pending operations in the array
    const hasPendingOperationsInArray = pendingCartOperations.length > 0;
    
    // If overlay is hidden and no operations in array, clear stale flags
    if (!overlayVisible && !hasPendingOperationsInArray) {
      // Clear stale flags that might be preventing overflow restoration
      if (isHandlingProductForm || pendingAddToCartFetch) {
        isHandlingProductForm = false;
        pendingAddToCartFetch = null;
      }
    }
    
    // Check if there are pending operations OR loading overlay is visible
    const hasPendingOperations = isHandlingProductForm || 
                                pendingAddToCartFetch || 
                                hasPendingOperationsInArray;
    
    
    if (hasPendingOperations || overlayVisible) {
      // Set overflow hidden when there are pending operations or loading is visible
      shadowBody.style.setProperty('overflow', 'hidden', 'important');
    } else {
      // Restore overflow when no pending operations and loading is hidden
      shadowBody.style.removeProperty('overflow');
    }
  }
  
  // Set up observer to reapply overflow style when cart drawer is refreshed
  function setupShadowBodyOverflowObserver() {
    const host = document.querySelector('#cart-drawer') || document.querySelector('cart-drawer');
    if (!host || !host.shadowRoot) return;
    
    // Use MutationObserver to watch for changes to shadow DOM
    const observer = new MutationObserver(() => {
      // Reapply overflow style if loading is active
      updateShadowBodyOverflow();
    });
    
    // Observe changes in shadow root
    observer.observe(host.shadowRoot, {
      childList: true,
      subtree: true,
      attributes: false
    });
    
    // Also listen for cart:change and cart:refresh events to reapply overflow
    document.addEventListener('cart:change', () => {
      // Small delay to allow DOM to update
      setTimeout(() => {
        updateShadowBodyOverflow();
      }, 100);
    }, true);
    
    document.addEventListener('cart:refresh', () => {
      // Small delay to allow DOM to update
      setTimeout(() => {
        updateShadowBodyOverflow();
      }, 100);
    }, true);
  }
  
  // Initialize observer when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(setupShadowBodyOverflowObserver, 500);
    });
  } else {
    setTimeout(setupShadowBodyOverflowObserver, 500);
  }

  // Track if loading is currently being hidden to prevent multiple calls
  let isHidingLoading = false;
  let overlayHidden = false;
  
  function hideCartLoading() {
    // Check if there are pending cart operations before hiding
    const hasPendingOperations = isHandlingProductForm || 
                                pendingAddToCartFetch || 
                                pendingCartOperations.length > 0;
    
    if (hasPendingOperations) {
      // Don't hide loading if there are pending operations
      // The fetch intercept handlers will hide it after operations complete
      return;
    }
    
    // Prevent multiple simultaneous calls
    if (isHidingLoading || overlayHidden) {
      return;
    }
    
    isHidingLoading = true;
    let overlay = null;
    
    // Try to find overlay in shadow DOM first
    const host = document.querySelector('#cart-drawer') || document.querySelector('cart-drawer');
    if (host && host.shadowRoot) {
      //
      overlay = host.shadowRoot.getElementById('cart-loading-overlay');
      if (!overlay) {
        // Try querySelector as fallback
        overlay = host.shadowRoot.querySelector('#cart-loading-overlay');
      }
    }
    
    // Fallback to regular DOM if not found in shadow DOM
    if (!overlay) {
      //
      overlay = document.getElementById('cart-loading-overlay');
    }
    
    // Also try document.querySelector as final fallback
    if (!overlay) {
      overlay = document.querySelector('#cart-loading-overlay');
    }
    
    if (overlay) {
      // Check if already hidden
      if (overlay.style.display === 'none') {
        // Already hidden - just ensure overflow is restored
        overlayHidden = true;
        isHidingLoading = false;
        updateShadowBodyOverflow();
        return;
      }
      
      // Add delay before hiding to ensure UI is fully updated
      setTimeout(() => {
        const cartDrawerItems = document.querySelector('.cart-drawer__items');
       
        overlay.style.display = 'none';
        overlayHidden = true;
        isHidingLoading = false;
        
        // Show checkout button price after hiding overlay
        showCheckoutButtonPrice();
        
        // IMPORTANT: Restore overflow after hiding overlay
        // This ensures overflow hidden is removed when loading is hidden
        updateShadowBodyOverflow();
      }, 500);
    } else {
      // Overlay not found - still restore overflow in case it was set
      isHidingLoading = false;
      updateShadowBodyOverflow();
    }
  }

  //=============================================================================
  // SECTION 3: QUANTITY MANAGEMENT
  //=============================================================================
  // Functions for managing 3-jar quantity selectors and updates
  //=============================================================================
  
  // Initialize custom 3-jar quantity selectors
  function initCustom3JarQuantitySelectors() {
    const quantitySelectors = document.querySelectorAll('.custom-3jar-quantity-selector');
    //
    
    quantitySelectors.forEach((selector, index) => {
      // Only add listener if not already initialized
      if (initializedQuantitySelectors.has(selector)) {
        //
        return;
      }
      
      const decreaseBtn = selector.querySelector('.custom-3jar-qty-decrease');
      const increaseBtn = selector.querySelector('.custom-3jar-qty-increase');
      const input = selector.querySelector('.custom-3jar-qty-input');
      
      if (!decreaseBtn || !increaseBtn || !input) {
        // Custom 3-jar quantity selector missing required elements
        return;
      }
      
      //
      
      const lineKey = input.dataset.lineKey;
      
      // Disable decrease button if value is 1
      const updateButtonStates = () => {
        const value = parseInt(input.value) || 1;
        decreaseBtn.disabled = value <= 1;
        increaseBtn.disabled = value >= (parseInt(input.max) || 10);
      };
      
      // Decrease button handler
      const handleDecrease = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const currentValue = parseInt(input.value) || 1;
        if (currentValue > 1) {
          const newValue = currentValue - 1;
          input.value = newValue;
          updateButtonStates();
          // Update cart if this is an existing 3-jar subscription (has lineKey)
          if (lineKey) {
            //
            update3JarQuantity(lineKey, newValue * 3);
          } else {

          }
        }
      };
      
      // Increase button handler
      const handleIncrease = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const currentValue = parseInt(input.value) || 1;
        const maxValue = parseInt(input.max) || 10;
        if (currentValue < maxValue) {
          const newValue = currentValue + 1;
          input.value = newValue;
          updateButtonStates();
          // Update cart if this is an existing 3-jar subscription (has lineKey)
          if (lineKey) {
            //
            update3JarQuantity(lineKey, newValue * 3);
          } else {

          }
        }
      };
      
      // Input change handler
      const handleInputChange = () => {
        updateButtonStates();
        // Update cart if this is an existing 3-jar subscription (has lineKey)
        if (lineKey) {
          const newValue = parseInt(input.value) || 1;
          update3JarQuantity(lineKey, newValue * 3);
        }
      };
      
      // Attach event listeners
      decreaseBtn.addEventListener('click', handleDecrease);
      increaseBtn.addEventListener('click', handleIncrease);
      input.addEventListener('change', handleInputChange);
      
      // Initialize button states
      updateButtonStates();
      
      // Mark as initialized
      initializedQuantitySelectors.add(selector);
    });
  }

  // Update cart quantity for existing 3-jar subscription
  async function update3JarQuantity(lineKey, actualQuantity) {
    try {
      showCartLoading();
      
      const updateUrl = `${window.Shopify?.routes?.root || '/'}cart/update.js`;
      const updates = {};
      updates[lineKey] = actualQuantity;
      
      // Get section IDs for cart refresh
      const sectionsToBundle = [];
      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
      if (cartDrawer) {
        let sectionElement = cartDrawer.closest('.shopify-section');
        if (!sectionElement) {
          const overlaySection = document.querySelector('[id^="shopify-section-"][class*="cart"]');
          if (overlaySection) {
            sectionElement = overlaySection;
          }
        }
        if (sectionElement && sectionElement.id) {
          sectionsToBundle.push(sectionElement.id.replace('shopify-section-', ''));
        }
      }
      
      if (window.themeVariables?.settings?.pageType === "cart") {
        const mainCartSection = document.querySelector('#shopify-section-main-cart, .shopify-section--main-cart');
        if (mainCartSection && mainCartSection.id) {
          const mainCartSectionId = mainCartSection.id.replace('shopify-section-', '');
          if (!sectionsToBundle.includes(mainCartSectionId)) {
            sectionsToBundle.push(mainCartSectionId);
          }
        }
      }
      
      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          updates: updates,
          sections: sectionsToBundle.length > 0 ? sectionsToBundle.join(',') : undefined
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update cart quantity');
      }
      
      let cartData = null;
      try {
        cartData = await response.json();
      } catch (error) {

        throw new Error('Failed to parse cart response');
      }
      
      // Fetch fresh cart data to ensure accurate totals
      const freshCartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (freshCartResponse.ok) {
        cartData = await freshCartResponse.json();
      }
      
      // Get sections - check update response first, then fetch fresh if needed
      let sectionsData = null;
      
      // First, try to parse sections from the update response
      if (cartData.sections) {
        if (typeof cartData.sections === 'string') {
          try {
            sectionsData = JSON.parse(cartData.sections);
          } catch (error) {

          }
        } else if (typeof cartData.sections === 'object') {
          sectionsData = { ...cartData.sections };
        }
      }
      
      // If sections weren't in response or are incomplete, fetch them fresh
      if (!sectionsData || sectionsToBundle.some(sid => !sectionsData[sid])) {
        if (!sectionsData) {
          sectionsData = {};
        }
        
        // Small delay to ensure cart is fully updated on server
        await new Promise(resolve => setTimeout(resolve, 150));
        
        for (const sid of sectionsToBundle) {
          // Only fetch if we don't already have this section
          if (!sectionsData[sid]) {
            try {
              const sectionUrl = `${window.Shopify?.routes?.root || '/'}?section_id=${sid}`;
              const sectionResponse = await fetch(sectionUrl);
              if (sectionResponse.ok) {
                const sectionHtml = await sectionResponse.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(sectionHtml, 'text/html');
                const sectionElement = doc.getElementById(`shopify-section-${sid}`);
                if (sectionElement) {
                  // Always use the full section HTML - cart drawer expects to extract cart-drawer from it
                  sectionsData[sid] = sectionElement.outerHTML;
                }
              }
            } catch (error) {

            }
          }
        }
      }
      
      // Attach sections to cart data
      if (sectionsData && Object.keys(sectionsData).length > 0) {
        cartData.sections = sectionsData;
      }
      
      // Dispatch cart change event
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "quantity:updated",
          cart: cartData
        }
      }));
      
      // Update cart page if needed
      if (window.themeVariables?.settings?.pageType === "cart" && sectionsData) {
        for (const [sectionId, sectionHtml] of Object.entries(sectionsData)) {
          const sectionElement = document.getElementById(`shopify-section-${sectionId}`);
          if (sectionElement) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHtml, 'text/html');
            const newSection = doc.querySelector(`#shopify-section-${sectionId}`);
            if (newSection) {
              sectionElement.innerHTML = newSection.innerHTML;
            }
          }
        }
        setTimeout(() => {
          initOTPConverter();
        }, 100);
      }
      
    } catch (error) {

      alert('Failed to update quantity. Please try again.');
    } finally {
      hideCartLoading();
    }
  }

  // Initialize the converter when DOM is ready
  //=============================================================================
  // SECTION 4: CART OPERATIONS
  //=============================================================================
  // Functions for removing cart items and initializing cart operations
  //=============================================================================
  
  function initOTPConverter() {
    const selects = document.querySelectorAll('.subscription-plan-select');
    
    selects.forEach(select => {
      // Only add listener if not already initialized
      if (!initializedSelects.has(select)) {
        select.addEventListener('change', handleSubscriptionChange);
        initializedSelects.add(select);
      }
    });
    
    // Initialize 3-jar conversion buttons
    const buttons = document.querySelectorAll('.convert-to-3jar-btn');
    
    buttons.forEach(button => {
      // Only add listener if not already initialized
      if (!initializedButtons.has(button)) {
        button.addEventListener('click', handleButtonClick);
        initializedButtons.add(button);
      }
    });
    
    // Initialize Subscribe and Save conversion buttons
    const subscriptionButtons = document.querySelectorAll('.convert-to-subscription-btn');
    
    subscriptionButtons.forEach(button => {
      // Only add listener if not already initialized
      if (!initializedButtons.has(button)) {
        button.addEventListener('click', handleSubscriptionButtonClick);
        initializedButtons.add(button);
      }
    });
    
    // Initialize custom 3-jar quantity selectors
    initCustom3JarQuantitySelectors();

    // Initialize 3-jar remove links
    init3JarRemoveLinks();

    updateProgressBarFromCart();
  }

  // Initialize remove links to use AJAX (for 3-jar, Subscribe and Save, and OTP items)
  function init3JarRemoveLinks() {
    const removeLinks = document.querySelectorAll('.remove-3jar-item, .remove-cart-item');
    
    removeLinks.forEach(link => {
      // Only add listener if not already initialized
      if (link.dataset.initialized === 'true') {
        return;
      }
      
      link.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const lineKey = link.dataset.lineKey;
        if (!lineKey) {
          // Fallback to regular link behavior
          window.location.href = link.href;
          return;
        }
        
        // Remove item using AJAX
        await removeCartItem(lineKey);
      });
      
      link.dataset.initialized = 'true';
    });
  }

  // Remove item from cart using AJAX (works for all item types)
  async function removeCartItem(lineKey) {
    try {
      showCartLoading();
      
      const updateUrl = `${window.Shopify?.routes?.root || '/'}cart/update.js`;
      const updates = {};
      updates[lineKey] = 0; // Set quantity to 0 to remove
      
      // Get section IDs for cart refresh
      const sectionsToBundle = [];
      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
      if (cartDrawer) {
        let sectionElement = cartDrawer.closest('.shopify-section');
        if (!sectionElement) {
          const overlaySection = document.querySelector('[id^="shopify-section-"][class*="cart"]');
          if (overlaySection) {
            sectionElement = overlaySection;
          }
        }
        if (sectionElement && sectionElement.id) {
          sectionsToBundle.push(sectionElement.id.replace('shopify-section-', ''));
        }
      }
      
      if (window.themeVariables?.settings?.pageType === "cart") {
        const mainCartSection = document.querySelector('#shopify-section-main-cart, .shopify-section--main-cart');
        if (mainCartSection && mainCartSection.id) {
          const mainCartSectionId = mainCartSection.id.replace('shopify-section-', '');
          if (!sectionsToBundle.includes(mainCartSectionId)) {
            sectionsToBundle.push(mainCartSectionId);
          }
        }
      }
      
      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          updates: updates,
          sections: sectionsToBundle.length > 0 ? sectionsToBundle.join(',') : undefined
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove item from cart');
      }
      
      let cartData = null;
      try {
        cartData = await response.json();
      } catch (error) {

        throw new Error('Failed to parse cart response');
      }
      
      // Fetch fresh cart data
      const freshCartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (freshCartResponse.ok) {
        cartData = await freshCartResponse.json();
      }
      
      // Get sections
      let sectionsData = null;
      if (sectionsToBundle.length > 0) {
        sectionsData = {};
        await new Promise(resolve => setTimeout(resolve, 150));
        
        for (const sid of sectionsToBundle) {
          try {
            const sectionUrl = `${window.Shopify?.routes?.root || '/'}?section_id=${sid}`;
            const sectionResponse = await fetch(sectionUrl);
            if (sectionResponse.ok) {
              const sectionHtml = await sectionResponse.text();
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionHtml, 'text/html');
              const sectionElement = doc.getElementById(`shopify-section-${sid}`);
              if (sectionElement) {
                sectionsData[sid] = sectionElement.outerHTML;
              }
            }
          } catch (error) {

          }
        }
      }
      
      // Parse sections from response if available
      if (cartData.sections) {
        if (typeof cartData.sections === 'string') {
          try {
            const parsedSections = JSON.parse(cartData.sections);
            sectionsData = { ...sectionsData, ...parsedSections };
          } catch (error) {

          }
        } else if (typeof cartData.sections === 'object') {
          sectionsData = { ...sectionsData, ...cartData.sections };
        }
      }
      
      // Attach sections to cart data
      if (sectionsData && Object.keys(sectionsData).length > 0) {
        cartData.sections = sectionsData;
      }
      
      // Dispatch cart change event
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "item:removed",
          cart: cartData
        }
      }));
      
      // Update progress bar with fresh cart data after removal
      setTimeout(() => {
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {

            updateShippingProgressBarWithRetry(cart, 3);
          })
          .catch(() => {});
      }, 500);
      
      // Update cart page if needed
      if (window.themeVariables?.settings?.pageType === "cart" && sectionsData) {
        for (const [sectionId, sectionHtml] of Object.entries(sectionsData)) {
          const sectionElement = document.getElementById(`shopify-section-${sectionId}`);
          if (sectionElement) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHtml, 'text/html');
            const newSection = doc.querySelector(`#shopify-section-${sectionId}`);
            if (newSection) {
              sectionElement.innerHTML = newSection.innerHTML;
            }
          }
        }
        setTimeout(() => {
          initOTPConverter();
        }, 100);
      }
      
    } catch (error) {

      alert('Failed to remove item. Please try again.');
    } finally {
      hideCartLoading();
    }
  }

  // Handle 3-jar button click
  async function handleButtonClick(event) {
    const button = event.target.closest('.convert-to-3jar-btn');
    if (!button) return;
    
    // Disable button during processing
    button.disabled = true;
    button.style.opacity = '0.6';
    
    const lineKey = button.dataset.lineKey;
    const variantId = button.dataset.variantId;
    const productHandle = button.dataset.productHandle;
    
    // Get quantity from custom quantity selector (bundle units)
    let displayQuantity = 1;
    const quantitySelector = button.closest('.exact-subscription-widget')?.querySelector('.custom-3jar-quantity-selector');
    if (quantitySelector) {
      const qtyInput = quantitySelector.querySelector('.custom-3jar-qty-input');
      if (qtyInput) {
        displayQuantity = parseInt(qtyInput.value) || 1;
      }
    }
    
    // Convert bundle units to actual cart units (1 bundle = 3 jars)
    const quantity = displayQuantity * 3;
    
    // Validate required data attributes
    if (!lineKey || !variantId || !productHandle) {
      hideCartLoading();
      button.disabled = false;
      button.style.opacity = '1';
      return;
    }
    
    // Show loading overlay
    showCartLoading();
    
    try {
      const widget = button.closest('.exact-subscription-widget');
      const sellingPlanIdFromWidget =
        widget?.dataset?.threeJarSellingPlanId ||
        widget?.querySelector('input[name="purchase_option"]:checked')?.dataset?.sellingPlanId ||
        null;
      await convertToSubscriptionPlan({
        lineKey,
        variantId,
        productHandle,
        quantity,
        selectedValue: '3jar',
        element: button,
        sellingPlanId: sellingPlanIdFromWidget
      });
    } catch (error) {
      alert(error.message || 'Failed to convert to 3-jar subscription. Please try again.');
    } finally {
      // Re-enable button
      button.disabled = false;
      button.style.opacity = '1';
      
      // Clear any stale flags and wait a bit for operations to complete before hiding
      setTimeout(() => {
        // Clear flags to ensure hideCartLoading() doesn't block
        isHandlingProductForm = false;
        pendingAddToCartFetch = null;
        overlayHidden = false;
        updateShadowBodyOverflow();
        hideCartLoading();
      }, 500);
    }
  }

  async function handleSubscriptionButtonClick(event) {
    const button = event.target.closest('.convert-to-subscription-btn');
    if (!button) return;
    
    // Disable button during processing
    button.disabled = true;
    button.style.opacity = '0.6';
    
    const lineKey = button.dataset.lineKey;
    const variantId = button.dataset.variantId;
    const productHandle = button.dataset.productHandle;
    const quantity = parseInt(button.dataset.quantity) || 1;
    const sellingPlanId = button.dataset.planId;
    const planType = button.dataset.planType || 'monthly';
    
    // Validate required data attributes
    if (!lineKey || !variantId || !productHandle) {
      hideCartLoading();
      button.disabled = false;
      button.style.opacity = '1';
      return;
    }
    
    // Show loading overlay
    showCartLoading();
    
    try {
      // Call the conversion function with "monthly" as the selected value
      await convertToSubscriptionPlan({
        lineKey,
        variantId,
        productHandle,
        quantity,
        selectedValue: planType,
        element: button,
        sellingPlanId: sellingPlanId
      });
    } catch (error) {
      alert(error.message || 'Failed to convert to subscription. Please try again.');
    } finally {
      // Re-enable button
      button.disabled = false;
      button.style.opacity = '1';
      
      // Clear any stale flags and wait a bit for operations to complete before hiding
      setTimeout(() => {
        // Clear flags to ensure hideCartLoading() doesn't block
        isHandlingProductForm = false;
        pendingAddToCartFetch = null;
        overlayHidden = false;
        updateShadowBodyOverflow();
        hideCartLoading();
      }, 500);
    }
  }

  //=============================================================================
  // SECTION 5: SUBSCRIPTION CONVERSION
  //=============================================================================
  // Functions for converting OTP products to subscription plans
  //=============================================================================
  
  // Handle subscription plan dropdown change
  async function handleSubscriptionChange(event) {
    const select = event.target;
    const selectedValue = select.value;
    
    if (!selectedValue) return; // No selection made
    
    // Disable select during processing
    select.disabled = true;
    select.style.opacity = '0.6';
    
    const lineKey = select.dataset.lineKey;
    const variantId = select.dataset.variantId;
    const productHandle = select.dataset.productHandle;
    const quantity = parseInt(select.dataset.quantity) || 1;
    
    // Try to get selling plan ID from data attributes first (preferred method)
    let targetSellingPlanId = null;
    if (selectedValue === 'monthly' && select.dataset.monthlyPlanId) {
      targetSellingPlanId = select.dataset.monthlyPlanId;
    } else if (selectedValue === '3jar' && select.dataset['3jarPlanId']) {
      targetSellingPlanId = select.dataset['3jarPlanId'];
    }
    
    // Also try to get from selected option's data attribute
    if (!targetSellingPlanId) {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption && selectedOption.dataset.planId) {
        targetSellingPlanId = selectedOption.dataset.planId;
      }
    }
    
    // Validate required data attributes
    if (!lineKey || !variantId || !productHandle) {
      hideCartLoading();
      select.disabled = false;
      select.style.opacity = '1';
      return;
    }
    
    // Show loading overlay
    showCartLoading();
    
    try {
      // Call the conversion function
      await convertToSubscriptionPlan({
        lineKey,
        variantId,
        productHandle,
        quantity,
        selectedValue,
        element: select,
        sellingPlanId: targetSellingPlanId
      });
    } catch (error) {
      alert(error.message || 'Failed to convert subscription plan. Please try again.');
    } finally {
      // Re-enable select
      select.disabled = false;
      select.style.opacity = '1';
      
      // Clear any stale flags and wait a bit for operations to complete before hiding
      setTimeout(() => {
        // Clear flags to ensure hideCartLoading() doesn't block
        isHandlingProductForm = false;
        pendingAddToCartFetch = null;
        overlayHidden = false;
        updateShadowBodyOverflow();
        hideCartLoading();
      }, 500);
    }
  }

  // Shared conversion function for both dropdown and button
  async function convertToSubscriptionPlan({ lineKey, variantId, productHandle, quantity, selectedValue, element, sellingPlanId }) {
    
    try {
      // Show loading state
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
      
      // Use selling plan ID if provided (from dropdown / button / widget — preferred; currency-agnostic)
      let targetSellingPlanId = sellingPlanId || null;

      const widget = element?.closest?.('.exact-subscription-widget') || document.querySelector('.exact-subscription-widget');
      if (!targetSellingPlanId && widget) {
        if (selectedValue === 'monthly' && widget.dataset.monthlySellingPlanId) {
          targetSellingPlanId = widget.dataset.monthlySellingPlanId;
        } else if (selectedValue === '3jar' && widget.dataset.threeJarSellingPlanId) {
          targetSellingPlanId = widget.dataset.threeJarSellingPlanId;
        }
      }

      // Legacy widget format: "planId:priceInCents" (USD-only); match by numeric plan id when second part looks like a small price
      if (!targetSellingPlanId && widget && widget.dataset.sellingPlans) {
        const sellingPlansData = widget.dataset.sellingPlans;
        const plans = sellingPlansData.split(',').filter(p => p.trim());
        for (const plan of plans) {
          const parts = plan.split(':');
          if (parts.length < 2) continue;
          const planId = parts[0].trim();
          const second = parseInt(parts[1], 10);
          if (!planId || Number.isNaN(second)) continue;
          if (selectedValue === 'monthly' && second === 9800) {
            targetSellingPlanId = planId;
            break;
          }
          if (selectedValue === '3jar' && second === 9000) {
            targetSellingPlanId = planId;
            break;
          }
        }
      }
      
      // Fallback: Fetch product data to get selling plans
      if (!targetSellingPlanId) {
        const productResponse = await fetch(`/products/${productHandle}.js`);
        if (!productResponse.ok) {
          throw new Error('Failed to fetch product data');
        }
        
        const productData = await productResponse.json();
        
        // Find the variant and its selling plan allocations
        if (!productData || !productData.variants || !Array.isArray(productData.variants)) {
          throw new Error('Invalid product data received');
        }
        
        const variant = productData.variants.find(v => v && v.id === parseInt(variantId));
        if (!variant) {
          throw new Error(`Variant ${variantId} not found in product`);
        }
        
        
        // Check if selling_plan_allocations exists and is an array
        if (!variant.selling_plan_allocations) {
          // Try alternative property names
          if (variant.selling_plan_allocation) {
            variant.selling_plan_allocations = Array.isArray(variant.selling_plan_allocation) 
              ? variant.selling_plan_allocation 
              : [variant.selling_plan_allocation];
          } else {
            throw new Error('No selling plans available for this variant. The product may not have subscription options.');
          }
        }
        
        if (!Array.isArray(variant.selling_plan_allocations) || variant.selling_plan_allocations.length === 0) {
          throw new Error('No selling plans available for this variant');
        }
      
        // Resolve plan by selling plan name (presentment-currency safe; product JSON may omit names)
        for (const allocation of variant.selling_plan_allocations) {
          if (!allocation || !allocation.selling_plan_id) {
            continue;
          }

          const planId = allocation.selling_plan_id;
          let planName = '';
          if (allocation.selling_plan) {
            const planObj = allocation.selling_plan;
            planName = String(planObj.name || planObj.title || planObj.description || '').toLowerCase();
          }

          if (selectedValue === 'monthly') {
            if (planName.includes('monthly') ||
                planName.includes('4 week') ||
                planName.includes('subscribe and save') ||
                planName.includes('28 day') ||
                planName.includes('28 serving')) {
              targetSellingPlanId = planId;
              break;
            }
          } else if (selectedValue === '3jar') {
            const isNameMatch = planName.includes('quarterly') ||
                planName.includes('3-jar') ||
                planName.includes('3 jar') ||
                planName.includes('12 week') ||
                planName.includes('best value') ||
                planName.includes('bundle') ||
                planName.includes('quarter');
            if (isNameMatch) {
              targetSellingPlanId = planId;
              break;
            }
          }
        }
      
        if (!targetSellingPlanId) {
          // Get allocation details for error message and debugging
          let allocationDetails = [];
          if (variant && variant.selling_plan_allocations) {
            allocationDetails = variant.selling_plan_allocations.map(a => {
              // Product JSON API uses selling_plan_id, not selling_plan object
              const planId = a?.selling_plan_id || 'N/A';
              const plan = a?.selling_plan || {};
              // Try multiple ways to get the plan name
              const planName = plan.name || plan.title || plan.description || 'Unknown';
              return {
                id: planId,
                price: a?.price || 0,
                name: planName,
                description: plan.description || '',
                perDeliveryPrice: a?.per_delivery_price || 0,
                // Also show the full allocation object for debugging
                fullPlan: plan,
                fullAllocation: a
              };
            });
          }
          
          
          const planNames = allocationDetails.map(a => `${a.name} (${(a.price || 0) / 100})`).join(', ') || 'None found';
          throw new Error(`Selling plan not found for "${selectedValue === 'monthly' ? 'Subscribe and Save' : '3 Jar Plan'}". Available plans: ${planNames}. Please try refreshing the page.`);
        }
      }
      
      // Get section IDs for cart refresh
      const sectionsToBundle = [];
      let sectionId = null;
      
      // Try to find cart drawer section
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Find the shopify-section parent
        let sectionElement = cartDrawer.closest('.shopify-section');
        if (!sectionElement) {
          // Cart drawer might be in overlay, try to find by ID pattern
          const overlaySection = document.querySelector('[id^="shopify-section-"][class*="cart"]');
          if (overlaySection) {
            sectionElement = overlaySection;
          }
        }
        
        if (sectionElement && sectionElement.id) {
          sectionId = sectionElement.id.replace('shopify-section-', '');
          sectionsToBundle.push(sectionId);
          
          // Notify cart drawer to prepare for section update
          document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          }));
        }
      }
      
      // Also add main-cart section if on cart page
      if (window.themeVariables?.settings?.pageType === "cart") {
        const mainCartSection = document.querySelector('#shopify-section-main-cart, .shopify-section--main-cart');
        if (mainCartSection && mainCartSection.id) {
          const mainCartSectionId = mainCartSection.id.replace('shopify-section-', '');
          if (!sectionsToBundle.includes(mainCartSectionId)) {
            sectionsToBundle.push(mainCartSectionId);
          }
        }
      }
      
      // Step 1: Remove the OTP line item from cart using cart/update.js
      const updateUrl = `${window.Shopify?.routes?.root || '/'}cart/update.js`;
      const updates = {};
      updates[lineKey] = 0; // Set quantity to 0 to remove
      
      const removeResponse = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          updates: updates,
          sections: sectionsToBundle.length > 0 ? sectionsToBundle.join(',') : undefined
        })
      });
      
      if (!removeResponse.ok) {
        const removeError = await removeResponse.json().catch(() => ({}));
        throw new Error(removeError.description || 'Failed to remove item from cart');
      }
      
      // Step 2: Add the same variant with the selected selling plan using cart/add.js
      const addUrl = `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      
      let finalQuantity = quantity;
      if (selectedValue === '3jar') {
        // Keep 3-jar cart quantity in actual units (3, 6, 9, ...)
        finalQuantity = quantity;
      }
      
      // Build form data for cart/add.js (it prefers FormData)
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', finalQuantity);
      formData.append('selling_plan', targetSellingPlanId);
      if (sectionsToBundle.length > 0) {
        formData.append('sections', sectionsToBundle.join(','));
      }
      
      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      
      if (!addResponse.ok) {
        const errorData = await addResponse.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add subscription to cart');
      }
      
      // Get response data - cart/add.js may return sections if we included them
      let addResponseData = null;
      try {
        addResponseData = await addResponse.json();
      } catch (error) {
        throw new Error('Failed to parse cart response');
      }
      
      // Fetch the full cart data
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      let finalCartData = null;
      if (cartResponse.ok) {
        finalCartData = await cartResponse.json();
      }
      
      // Check if addResponseData has sections (some Shopify setups return it)
      let sectionsData = addResponseData.sections || null;
      
      // If we didn't get sections from add response, fetch them separately
      if (!sectionsData && sectionsToBundle.length > 0) {
        sectionsData = {};
        
        // Fetch each section HTML
        for (const sid of sectionsToBundle) {
          try {
            const sectionUrl = `${window.Shopify?.routes?.root || '/'}?section_id=${sid}`;
            const sectionResponse = await fetch(sectionUrl);
            if (sectionResponse.ok) {
              const sectionHtml = await sectionResponse.text();
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionHtml, 'text/html');
              const sectionElement = doc.getElementById(`shopify-section-${sid}`);
              
              if (sectionElement) {
                // Provide the full section HTML - cart drawer will extract what it needs
                sectionsData[sid] = sectionElement.outerHTML;
              }
            }
          } catch (error) {
            // Silently fail - cart drawer will refresh itself
          }
        }
      }
      
      // Update the cart UI with the new data
      if (!finalCartData) {
        throw new Error('Failed to get updated cart data');
      }
      
      if (sectionsData && Object.keys(sectionsData).length > 0) {
        // Attach sections to cart object (cart drawer expects cart.sections[sectionId])
        finalCartData.sections = sectionsData;
        
        // We have sections data, dispatch cart:change event for cart drawer
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "otp-to-subscription:converted",
            cart: finalCartData
          }
        }));
        
        // Update progress bar using the cart data we already have from conversion
        // No need to fetch again - we already have finalCartData
        setTimeout(() => {


          if (finalCartData && finalCartData.items && finalCartData.items.length > 0) {
            updateShippingProgressBarWithRetry(finalCartData, 3);
          } else {

            // Fallback: fetch if finalCartData is somehow empty
            fetch('/cart.js')
              .then(response => response.json())
              .then(cart => {
                if (cart && cart.items && cart.items.length > 0) {
                  updateShippingProgressBarWithRetry(cart, 3);
                }
              })
              .catch(() => {});
          }
        }, 300); // Small delay to ensure DOM is updated
        
        // Also update cart page if we're on it
        if (window.themeVariables?.settings?.pageType === "cart") {
          // Update the main cart section
          for (const [sectionId, sectionHtml] of Object.entries(sectionsData)) {
            const sectionElement = document.getElementById(`shopify-section-${sectionId}`);
            if (sectionElement) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionHtml, 'text/html');
              const newSection = doc.querySelector(`#shopify-section-${sectionId}`);
              if (newSection) {
                sectionElement.innerHTML = newSection.innerHTML;
              }
            }
          }
          
          // Re-initialize the converter after DOM update
          setTimeout(() => {
            initOTPConverter();
          }, 100);
        }
      } else if (finalCartData) {
        // Fallback: dispatch cart change without sections (cart drawer will refresh itself)
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "otp-to-subscription:converted",
            cart: finalCartData
          }
        }));
        
        // Update progress bar using the cart data we already have from conversion
        setTimeout(() => {


          if (finalCartData && finalCartData.items && finalCartData.items.length > 0) {
            updateShippingProgressBarWithRetry(finalCartData, 3);
          } else {

            // Fallback: fetch if finalCartData is somehow empty
            fetch('/cart.js')
              .then(response => response.json())
              .then(cart => {
                if (cart && cart.items && cart.items.length > 0) {
                  updateShippingProgressBarWithRetry(cart, 3);
                }
              })
              .catch(() => {});
          }
        }, 300); // Small delay to ensure DOM is updated
        
        // For cart page, trigger a refresh
        if (window.themeVariables?.settings?.pageType === "cart") {
          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        }
      }
      
      // Re-initialize converter to handle new dropdowns and buttons
      setTimeout(() => {
        initOTPConverter();
      }, 200);
      
      // End loading state
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      
    } catch (error) {

      
      // End loading state
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      
      // Show error message to user
      const errorMsg = document.createElement('p');
      errorMsg.className = 'subscription-converter-error';
      errorMsg.style.cssText = 'color: #BF1515; font-size: 14px; margin-top: 0.5rem;';
      errorMsg.textContent = error.message || 'Failed to convert to subscription. Please try again.';
      
      // Remove any existing error message
      const parentElement = element.parentElement || element.closest('.cart-item') || element.closest('cart-item');
      if (parentElement) {
        const existingError = parentElement.querySelector('.subscription-converter-error');
        if (existingError) {
          existingError.remove();
        }
        parentElement.appendChild(errorMsg);
      }
      
      // Reset select if it's a select element
      if (element.tagName === 'SELECT') {
        element.value = '';
      }
      
      // Re-throw error so calling functions can handle it
      throw error;
    }
  }

  // Initialize on DOM ready
  function initialize() {
    initOTPConverter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Get cart drawer section ID
  function getCartDrawerSectionId() {
    const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
    if (!cartDrawer) return null;
    
    // Try multiple methods to find section ID
    let sectionId = null;
    
    // Method 1: Check closest shopify-section
    const section = cartDrawer.closest('.shopify-section');
    if (section && section.id) {
      sectionId = section.id.replace('shopify-section-', '');
    }
    
    // Method 2: Check parent elements
    if (!sectionId) {
      let parent = cartDrawer.parentElement;
      let depth = 0;
      while (parent && depth < 10) {
        if (parent.classList?.contains('shopify-section') || (parent.id && parent.id.startsWith('shopify-section-'))) {
          sectionId = parent.id.replace('shopify-section-', '');
          break;
        }
        parent = parent.parentElement;
        depth++;
      }
    }
    
    // Method 3: Search all sections
    if (!sectionId) {
      const allSections = document.querySelectorAll('.shopify-section');
      for (const sec of allSections) {
        if (sec.querySelector('cart-drawer') || sec.querySelector('#cart-drawer')) {
          sectionId = sec.id.replace('shopify-section-', '');
          break;
        }
      }
    }
    
    return sectionId;
  }

  // Track if drawer was opened by add to cart to prevent refresh conflict
  let drawerOpenedByAddToCart = false;
  
  // Refresh cart when drawer opens to ensure we have latest data
  //=============================================================================
  // SECTION 6: FETCH INTERCEPTION & SETUP
  //=============================================================================
  // Functions for intercepting cart API calls and setting up event listeners
  //=============================================================================
  
  function setupCartDrawerRefresh() {
    const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
    if (cartDrawer) {
      // Listen for drawer opening
      cartDrawer.addEventListener('dialog:before-show', async () => {
        // Skip refresh if drawer was opened by add to cart (will be updated by cart:change event)
        if (drawerOpenedByAddToCart) {
          drawerOpenedByAddToCart = false; // Reset flag
          return;
        }
        
        showCartLoading();
        
        try {
          // Fetch fresh cart data
          const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache'
            }
          });
          
          if (cartResponse.ok) {
            const cartData = await cartResponse.json();
            
            // Get cart drawer section ID
            const sectionId = getCartDrawerSectionId();
            if (sectionId) {
              const sectionUrl = `${window.Shopify?.routes?.root || '/'}?section_id=${sectionId}`;
              const sectionResponse = await fetch(sectionUrl, {
                cache: 'no-store',
                headers: {
                  'Cache-Control': 'no-cache'
                }
              });
              if (sectionResponse.ok) {
                const sectionHtml = await sectionResponse.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(sectionHtml, 'text/html');
                const sectionElement = doc.getElementById(`shopify-section-${sectionId}`);
                if (sectionElement) {
                  const sectionsData = {};
                  sectionsData[sectionId] = sectionElement.outerHTML;
                  cartData.sections = sectionsData;
                  
                  // Dispatch cart change to update drawer
                  document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
                    bubbles: true,
                    detail: {
                      baseEvent: "drawer:opened",
                      cart: cartData
                    }
                  }));
                }
              }
            }
          }
        } catch (error) {
          // Error fetching cart - still hide loading
        } finally {
          // When drawer is opened normally (not by add-to-cart), clear any stale flags
          // and hide loading regardless of pending operations
          // Only check pendingCartOperations array (not boolean flags) because flags can be stale
          const hasPendingOperations = pendingCartOperations.length > 0;
          
          if (!hasPendingOperations) {
            // No pending operations - clear any stale flags and hide loading
            // This ensures loading hides even if boolean flags are stale from previous operations
            isHandlingProductForm = false;
            pendingAddToCartFetch = null;
            overlayHidden = false;
            updateShadowBodyOverflow();
            hideCartLoading();
          } else {
            // Has pending operations - skip hide, will hide after operations complete
            // But still clear overlayHidden flag to allow future hide calls
            overlayHidden = false;
          }
        }
      });
    }
  }

  // Track if subscription widget is initialized
  let subscriptionWidgetInitialized = false;
  
  // Handle subscription product add to cart
  function setupSubscriptionProductAddToCart() {
    // Prevent duplicate initialization
    if (subscriptionWidgetInitialized) {
      return;
    }

    /** PDP widget only — cart line-items also use .exact-subscription-widget (drawer); first global match was wrong. */
    function resolveSubscriptionPdpWidget() {
      const mainEl = document.querySelector('main#main') || document.querySelector('main');
      if (mainEl) {
        const inMain = mainEl.querySelector('.exact-subscription-widget');
        if (inMain) return inMain;
      }
      const atc = document.getElementById('customAddToCart');
      if (atc) {
        const nearBtn = atc.closest('.exact-subscription-widget');
        if (nearBtn) return nearBtn;
      }
      return null;
    }
    
    function initSubscriptionWidget() {
      const widget = resolveSubscriptionPdpWidget();
      if (!widget) {
        return; // Widget not found, skip initialization
      }

      const addToCartBtnEarly = widget.querySelector('#customAddToCart') || document.getElementById('customAddToCart');
      if (!addToCartBtnEarly) {
        return;
      }

      if (widget.dataset.initialized === 'true') {
        return;
      }
      widget.dataset.initialized = 'true';
      
      const variantId = widget ? widget.dataset.variantId : null;
      let selectedSellingPlanId = null;
      let selectedQuantity = 1;

      const subRows = widget.querySelectorAll('.sub-row');

      /* Handle subscription option selection (scoped to this widget) */
      subRows.forEach(row => {
        row.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          widget.querySelectorAll('.sub-row')
            .forEach(r => r.classList.remove('active'));

          row.classList.add('active');

          const input = row.querySelector('input');
          if (input) {
            const planId = input.dataset.sellingPlanId;
            selectedSellingPlanId = (planId && planId !== 'null' && planId !== '') ? planId : null;
            selectedQuantity = parseInt(input.dataset.quantity) || 1;
          }
        });
      });

      /* Initial selected plan (3‑Jar by default) */
      const activeInput = widget.querySelector('.sub-row.active input');
      if (activeInput) {
        const planId = activeInput.dataset.sellingPlanId;
        selectedSellingPlanId = (planId && planId !== 'null' && planId !== '') ? planId : null;
        selectedQuantity = parseInt(activeInput.dataset.quantity) || 1;
      }

      const addToCartBtn = addToCartBtnEarly;
      
      async function handleAddToCart(e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        if (!variantId) {
          alert('Product variant not found. Please refresh the page.');
          return;
        }

        const variantAvailable = widget ? widget.dataset.variantAvailable === 'true' : true;
        if (!variantAvailable) {
          alert('This product variant is currently unavailable.');
          return;
        }

        // Track if loading end has been dispatched for this operation
        let loadingEndDispatched = false;
        const dispatchLoadingEnd = () => {
          if (!loadingEndDispatched) {
            loadingEndDispatched = true;
            document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          }
        };

        // IMMEDIATELY show loading overlay and open cart drawer
        if (window.showCartLoading) {
          window.showCartLoading();
        }
        
        const cartDrawer = document.getElementById('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          drawerOpenedByAddToCart = true;
          cartDrawer.show();
        }
        
        // Also trigger theme:loading:start for consistency
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

        try {
          if (!variantAvailable) {
            throw new Error('This product variant is currently unavailable.');
          }

          if (selectedSellingPlanId && (selectedSellingPlanId === 'null' || selectedSellingPlanId === '')) {
            selectedSellingPlanId = null;
          }

          const cartData = {
            items: [{
              id: variantId,
              quantity: selectedQuantity
            }]
          };

          if (selectedSellingPlanId && selectedSellingPlanId !== 'null' && selectedSellingPlanId !== '') {
            cartData.items[0].selling_plan = selectedSellingPlanId;
          }

          // Clear cart and add to cart
          await fetch('/cart/clear.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          }).catch(() => {});

          await new Promise(resolve => setTimeout(resolve, 100));

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(cartData)
          });

          const responseData = await response.json();
          if (!response.ok) {
            throw new Error(responseData.description || responseData.message || 'Failed to add to cart');
          }

          // Wait a bit for cart to be fully updated
          await new Promise(resolve => setTimeout(resolve, 200));

          // Fetch updated cart to ensure it's synced
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();

          // Store the expected item count to verify UI update
          const expectedItemCount = cart.item_count;

          // Dispatch cart:refresh - this will fetch the section using the drawer's internal section ID
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

          // Also dispatch cart:change to update cart count and other components
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: 'force_open_drawer',
              cart: cart
            }
          }));

          // Wait for UI to actually update by checking if the drawer has the new item
          let updateCheckAttempts = 0;
          const maxAttempts = 20; // 2 seconds max wait
          
          const checkUIUpdate = () => {
            updateCheckAttempts++;
            
            // Check if cart drawer has been updated with the new item
            const drawerItems = cartDrawer?.querySelectorAll('line-item') || 
                                cartDrawer?.shadowRoot?.querySelectorAll('line-item') || [];
            
            // Also check cart count
            const cartCount = document.querySelector('cart-count');
            const currentCount = cartCount ? parseInt(cartCount.textContent) || 0 : 0;
            
            // Check if UI has updated (either item count matches or we found items in drawer)
            if (currentCount === expectedItemCount || drawerItems.length > 0) {
              // UI has updated, hide loading
              dispatchLoadingEnd();
            } else if (updateCheckAttempts < maxAttempts) {
              // Keep checking
              setTimeout(checkUIUpdate, 100);
            } else {
              // Timeout - hide loading anyway

              dispatchLoadingEnd();
            }
          };
          
          // Start checking after a short delay to allow events to process
          setTimeout(checkUIUpdate, 200);

        } catch (error) {

          alert('Failed to add to cart: ' + error.message);
          // Hide loading overlay on error
          dispatchLoadingEnd();
        }
      }

      // Attach handler to main button (only if not already attached)
      if (!addToCartBtn.dataset.clickHandlerAttached) {
        addToCartBtn.dataset.clickHandlerAttached = 'true';
        addToCartBtn.addEventListener('click', handleAddToCart);
      }

      const otp1JarLink = widget.querySelector('#otp1JarLink');
      if (otp1JarLink) {
        otp1JarLink.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          widget.querySelectorAll('.sub-row').forEach(r => r.classList.remove('active'));
          selectedSellingPlanId = null;
          selectedQuantity = 1;
          handleAddToCart();
        });
      }
      const otp6JarsLink = widget.querySelector('#otp6JarsLink');
      if (otp6JarsLink) {
        otp6JarsLink.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          widget.querySelectorAll('.sub-row').forEach(r => r.classList.remove('active'));
          selectedSellingPlanId = null;
          selectedQuantity = 6;
          handleAddToCart();
        });
      }
    }
    
    // Initialize on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initSubscriptionWidget);
    } else {
      initSubscriptionWidget();
    }
    
    // Re-try when DOM changes (e.g. ATC injected after first paint). Idempotent via widget.dataset.initialized.
    const observer = new MutationObserver(() => {
      const w = resolveSubscriptionPdpWidget();
      if (w && w.dataset.initialized !== 'true') {
        initSubscriptionWidget();
      }
    });

    const subscriptionObserverRoot = document.body || document.documentElement;
    if (subscriptionObserverRoot) {
      observer.observe(subscriptionObserverRoot, {
        childList: true,
        subtree: true
      });
    } else {
      document.addEventListener('DOMContentLoaded', function attachSubObserver() {
        document.removeEventListener('DOMContentLoaded', attachSubObserver);
        if (document.body) {
          observer.observe(document.body, { childList: true, subtree: true });
        }
      }, { once: true });
    }
  }

  // Track if interceptors are already set up
  let interceptorsSetup = false;
  
  // Intercept add to cart to immediately open drawer with loading
  function setupAddToCartIntercept() {
    // Prevent duplicate setup
    if (interceptorsSetup) {
      return;
    }
    interceptorsSetup = true;
    
    // Note: isHandlingProductForm, pendingAddToCartFetch, pendingCartOperations, 
    // pendingCartJsCall are defined at the top level of the IIFE
    
    // Listen for line-item quantity changes to show loading overlay
    document.addEventListener('line-item:will-change', (event) => {
      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        // Open drawer if not already open and show loading
        showCartLoading();
        // Only show drawer if it's not already visible
        if (!cartDrawer.hasAttribute('open')) {
          cartDrawer.show();
        }
      } else {
        // Drawer not found, just show loading
        showCartLoading();
      }
    }, true);
    
    // Listen for line-item change completion to hide loading
    document.addEventListener('line-item:change', (event) => {
      // Wait a bit for cart drawer to update, then hide loading
      setTimeout(() => {
        hideCartLoading();
      }, 500);
    }, true);
    
    // Listen for line-item quantity changes to show loading overlay
    // This handles OTP and Subscribe and Save quantity changes
    document.addEventListener('line-item:will-change', (event) => {
      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        // Open drawer if not already open and show loading
        showCartLoading();
        // Only show drawer if it's not already visible
        if (!cartDrawer.hasAttribute('open')) {
          cartDrawer.show();
        }
      } else {
        // Drawer not found, just show loading
        showCartLoading();
      }
    }, true);
    
    // Listen for line-item change completion to hide loading
    document.addEventListener('line-item:change', (event) => {
      // Wait a bit for cart drawer to update, then hide loading
      setTimeout(() => {
        hideCartLoading();
      }, 500);
    }, true);
    
    // Use a single handler function that we can check for duplicates
    const loadingEndHandler = () => {
      // Don't hide loading if we're handling a ProductForm submission or have pending operations
      // The fetch intercept will handle hiding it after cart drawer updates
      const hasPendingOperations = isHandlingProductForm || 
                                  pendingAddToCartFetch || 
                                  pendingCartOperations.length > 0;
      
      if (!hasPendingOperations) {
        //
        hideCartLoading();
      } else {
        //
        //
      }
    };
    
    // Intercept form submissions to cart/add.js - set flag BEFORE fetch happens
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form && form.action && form.action.includes('/cart/add')) {
        // Mark that we're about to handle a ProductForm submission
        // This prevents cart.js calls from hiding loading prematurely
        isHandlingProductForm = true;
        pendingAddToCartFetch = true;
        
        const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          // Immediately open drawer and show loading
          showCartLoading();
          cartDrawer.show();
        }
      }
    }, true); // Use capture phase to catch early
    
    // Also listen for theme:loading:start to set flag early
    document.addEventListener('theme:loading:start', (event) => {
      // Check if this is from a product form submission
      const form = document.querySelector('product-form form[action*="/cart/add"]');
      if (form) {
        // Check if it's from homepage/featured product
        const formId = form.id ? String(form.id) : '';
        const isHomepage = window.location.pathname === '/' || window.location.pathname === '/index';
        const isFeaturedProduct = formId.includes('featured-product') || 
                                 formId.includes('product-form-featured-product') ||
                                 form.closest('section[id*="featured-product"], section[class*="featured-product"]');
        
        if (isHomepage || isFeaturedProduct) {
          isHandlingProductForm = true;
          pendingAddToCartFetch = true;
        }
      }
    }, true);
    
    // Listen for add to cart events from product forms
    document.addEventListener('variant:add', (event) => {
      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        // Immediately open drawer and show loading
        showCartLoading();
        cartDrawer.show();
      }
    }, true); // Use capture phase to catch early
    
    // Intercept fetch calls to cart/add.js and cart.js
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      
      
      // Intercept cart.js calls - delay them until after add/change/update operations complete
      if (url.includes('/cart.js') && !url.includes('/cart/add.js') && !url.includes('/cart/change.js') && !url.includes('/cart/update.js')) {
        const isHomepage = window.location.pathname === '/' || window.location.pathname === '/index';
        
        // Check if there's a pending cart operation (add, change, update)
        const hasPendingOperation = pendingCartOperations.length > 0 || 
                                   isHandlingProductForm || 
                                   pendingAddToCartFetch;
        
        //
        
        // Check if there's a form submission in progress
        const forms = document.querySelectorAll('product-form form[action*="/cart/add"]');
        let hasPendingSubmission = false;
        
        for (const form of forms) {
          const formId = form.id ? String(form.id) : '';
          const isFeaturedProduct = formId.includes('featured-product') || 
                                   formId.includes('product-form-featured-product') ||
                                   form.closest('section[id*="featured-product"], section[class*="featured-product"]');
          
          const submitButton = form.querySelector('button[type="submit"]');
          const isButtonBusy = submitButton && submitButton.getAttribute('aria-busy') === 'true';
          
          if ((isHomepage || isFeaturedProduct) && (isButtonBusy || isFeaturedProduct)) {
            hasPendingSubmission = true;
            break;
          }
        }
        
        //
        
        // If there's a pending operation or submission, delay this cart.js call
        if (hasPendingOperation || hasPendingSubmission) {
          // Store the cart.js call to execute later
          pendingCartJsCall = () => {
            return originalFetch.apply(this, args);
          };
          
          //
          
          // Set flags to prevent loading hide
          if (!isHandlingProductForm) {
            isHandlingProductForm = true;
            pendingAddToCartFetch = true;
            //
          }
          
          // Return a promise that resolves immediately (don't block)
          return Promise.resolve(new Response('{}', { status: 200, headers: { 'Content-Type': 'application/json' } }));
        } else {
          //
        }
      }
      
      // Intercept clear.js calls
      if (url.includes('/cart/clear.js')) {
        const isHomepage = window.location.pathname === '/' || window.location.pathname === '/index';
        
        // For clear.js calls on homepage, set flags to prevent loading hide
        if (isHomepage) {
          isHandlingProductForm = true;
          pendingAddToCartFetch = true;
          //
        }
        
        //
        return originalFetch.apply(this, args);
      }
      
      // Track cart operations (add, change, update) - MUST be before the add.js check below
      if (url.includes('/cart/add.js') || url.includes('/cart/add') || 
          url.includes('/cart/change.js') || url.includes('/cart/update.js')) {
        // Mark that we have a pending cart operation
        const operationType = url.includes('/cart/add') ? 'add' : 
                             url.includes('/cart/change') ? 'change' : 'update';
        pendingCartOperations.push(operationType);
        
        //
        
        // Show loading overlay for quantity changes (change.js) and updates (update.js)
        // This handles OTP and Subscribe and Save quantity changes
        if (operationType === 'change' || operationType === 'update') {
          const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
          if (cartDrawer && typeof cartDrawer.show === 'function') {
            // Open drawer if not already open and show loading
            showCartLoading();
            // Only show drawer if it's not already visible
            if (!cartDrawer.hasAttribute('open')) {
              cartDrawer.show();
            }
          } else {
            // Drawer not found, just show loading
            showCartLoading();
          }
        }
        
        // Set flags
        isHandlingProductForm = true;
        pendingAddToCartFetch = true;
        //
      }
      
      // Handle cart/change.js and cart/update.js completion
      if (url.includes('/cart/change.js') || url.includes('/cart/update.js')) {
        const operationType = url.includes('/cart/change') ? 'change' : 'update';
        
        // Execute the fetch and handle completion
        const fetchPromise = originalFetch.apply(this, args);
        
        fetchPromise.then(async (response) => {
          if (response.ok) {
            // Remove operation from pending list
            const operationIndex = pendingCartOperations.indexOf(operationType);
            if (operationIndex > -1) {
              pendingCartOperations.splice(operationIndex, 1);
            }
            
            // Wait for cart drawer to update
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Check if cart drawer has been updated
            const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
            let attempts = 0;
            const maxAttempts = 10;
            const checkUpdate = () => {
              attempts++;
              const drawerItems = cartDrawer?.querySelectorAll('line-item') || 
                                cartDrawer?.shadowRoot?.querySelectorAll('line-item') || [];
              
              if (drawerItems.length > 0 || attempts >= maxAttempts) {
                // Cart drawer updated or timeout - clear flags and hide loading
                // Reset flags BEFORE hiding to ensure hideCartLoading() doesn't block
                if (pendingCartOperations.length === 0) {
                  isHandlingProductForm = false;
                  pendingAddToCartFetch = null;
                }
                // Update shadow body overflow before hiding
                updateShadowBodyOverflow();
                setTimeout(() => {
                  hideCartLoading();
                }, 500);
              } else {
                setTimeout(checkUpdate, 100);
              }
            };
            
            setTimeout(checkUpdate, 200);
          } else {
            // Error - hide loading immediately
            const operationIndex = pendingCartOperations.indexOf(operationType);
            if (operationIndex > -1) {
              pendingCartOperations.splice(operationIndex, 1);
            }
            isHandlingProductForm = false;
            pendingAddToCartFetch = null;
            updateShadowBodyOverflow();
            setTimeout(() => {
              hideCartLoading();
            }, 500);
          }
        }).catch((error) => {
          // Error - hide loading
          const operationIndex = pendingCartOperations.indexOf(operationType);
          if (operationIndex > -1) {
            pendingCartOperations.splice(operationIndex, 1);
          }
          isHandlingProductForm = false;
          pendingAddToCartFetch = null;
          updateShadowBodyOverflow();
          setTimeout(() => {
            hideCartLoading();
          }, 500);
        });
        
        return fetchPromise;
      }
      
      // Continue to add.js specific handling below
      
      if (url.includes('/cart/add.js') || url.includes('/cart/add')) {
        const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          // Mark that drawer was opened by add to cart to prevent refresh conflict
          drawerOpenedByAddToCart = true;
          
          // Check if this is FormData (ProductForm submission) or JSON
          const isFormData = args[1]?.body instanceof FormData;
          let shouldClearCart = false;
          let variantId = null;
          
          if (isFormData) {
            // Mark that we're handling a ProductForm submission
            isHandlingProductForm = true;
            pendingAddToCartFetch = true;
            
            // Extract variant ID from FormData
            const formData = args[1].body;
            variantId = formData.get('id');
            
            // Check if this is from the homepage featured product section
            // Look for the form that triggered this fetch
            const forms = document.querySelectorAll('product-form form');
            for (const form of forms) {
              const formId = form.id ? String(form.id) : '';
              // Check if it's from featured product section (homepage)
              if (formId && (formId.includes('featured-product') || formId.includes('product-form-featured-product'))) {
                shouldClearCart = true;
                break;
              }
              // Also check if form is within a featured-product section
              const featuredSection = form.closest('section[id*="featured-product"], section[class*="featured-product"]');
              if (featuredSection) {
                shouldClearCart = true;
                break;
              }
              // Check if we're on homepage (index page)
              if (window.location.pathname === '/' || window.location.pathname === '/index') {
                shouldClearCart = true;
                break;
              }
            }
          } else if (args[1]?.body) {
            // JSON body - check if it's the subscription product
            try {
              const body = typeof args[1].body === 'string' ? JSON.parse(args[1].body) : args[1].body;
              if (body.items && body.items.length > 0) {
                variantId = body.items[0].id;
                // Clear cart for JSON add-to-cart calls (these are from custom handlers)
                shouldClearCart = true;
              }
            } catch (e) {
              // Not JSON, skip
            }
          }
          
          // Immediately open drawer and show loading
          showCartLoading();
          cartDrawer.show();
          
          // For ProductForm submissions, wait for cart drawer to update before hiding loading
          const fetchPromise = (async () => {
            // Clear cart first if needed
            if (shouldClearCart) {
              try {
                // Use originalFetch directly to bypass our intercept (which would set flags)
                // We want clear.js to execute but not trigger any loading state changes
                await originalFetch('/cart/clear.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                  }
                });
                //
                // Small delay to ensure cart is cleared
                await new Promise(resolve => setTimeout(resolve, 100));
              } catch (error) {

              }
            }
            
            // Now execute the original fetch
            return originalFetch.apply(this, args);
          })();
          
          // After fetch completes, wait for cart drawer to update
          fetchPromise.then(async (response) => {
            if (response.ok) {
              // Remove this operation from pending list
              const operationIndex = pendingCartOperations.indexOf('add');
              if (operationIndex > -1) {
                pendingCartOperations.splice(operationIndex, 1);
              }
              
              // IMPORTANT: Clear flags IMMEDIATELY after add.js completes successfully
              // Don't wait for drawer update - clear them now to prevent hideCartLoading() from blocking
              isHandlingProductForm = false;
              pendingAddToCartFetch = null;
              
              // Execute any pending cart.js call now (after add.js completes)
              if (pendingCartJsCall && pendingCartOperations.length === 0) {
                try {
                  await pendingCartJsCall();
                  pendingCartJsCall = null;
                } catch (error) {
                  // Error executing pending cart.js
                }
              }
              
              // Wait a bit for cart drawer to update
              await new Promise(resolve => setTimeout(resolve, 300));
              
              // Check if cart drawer has been updated
              let attempts = 0;
              const maxAttempts = 20;
              const checkUpdate = () => {
                attempts++;
                const drawerItems = cartDrawer?.querySelectorAll('line-item') || 
                                  cartDrawer?.shadowRoot?.querySelectorAll('line-item') || [];
                
                if (drawerItems.length > 0 || attempts >= maxAttempts) {
                  // Cart drawer has updated or timeout reached
                  // Update progress bar with fresh cart data (with retry if element doesn't exist)
                  
                  // Wait longer for cart to be fully updated on server and progress bar element to be rendered
                  setTimeout(() => {
                    fetch('/cart.js')
                      .then(response => response.json())
                      .then(cart => {
                        // Only update progress bar if cart has items
                        if (cart.items && cart.items.length > 0) {
                          // Try to update, with retry if element doesn't exist
                          updateShippingProgressBarWithRetry(cart, 5); // Increased retries for homepage
                        } else {
                          // Retry fetching cart after a delay if cart is empty
                          setTimeout(() => {
                            fetch('/cart.js')
                              .then(response => response.json())
                              .then(retryCart => {
                                if (retryCart.items && retryCart.items.length > 0) {
                                  updateShippingProgressBarWithRetry(retryCart, 5);
                                }
                              })
                              .catch(() => {});
                          }, 500);
                        }
                      })
                      .catch(() => {});
                  }, 800); // Increased delay to ensure cart is fully updated
                  
                  // Update shadow body overflow before hiding
                  updateShadowBodyOverflow();
                  
                  // Ensure overlayHidden is reset before hiding
                  overlayHidden = false;
                  
                  setTimeout(() => {
                    hideCartLoading();
                  }, 1000); // 1 second delay as requested
                } else {
                  setTimeout(checkUpdate, 100);
                }
              };
              
              setTimeout(checkUpdate, 200);
            } else {
              // Error - remove operation and reset flags
              const operationIndex = pendingCartOperations.indexOf('add');
              if (operationIndex > -1) {
                pendingCartOperations.splice(operationIndex, 1);
              }
              isHandlingProductForm = false;
              pendingAddToCartFetch = null;
              pendingCartJsCall = null;
              //
              setTimeout(() => {
                hideCartLoading();
              }, 500);
            }
          }).catch((error) => {
            // Error - remove operation and reset flags
            const operationIndex = pendingCartOperations.indexOf('add');
            if (operationIndex > -1) {
              pendingCartOperations.splice(operationIndex, 1);
            }
            isHandlingProductForm = false;
            pendingAddToCartFetch = null;
            pendingCartJsCall = null;
            //
            setTimeout(() => {
              hideCartLoading();
            }, 500);
          });
          
          return fetchPromise;
        }
        // Execute the fetch - loading will be hidden by theme:loading:end event
        return originalFetch.apply(this, args);
      }
      return originalFetch.apply(this, args);
    };
    
    // Also listen for cart:change with force_open_drawer
    document.addEventListener('cart:change', (event) => {
      if (event.detail?.onSuccessDo === 'force_open_drawer' || event.detail?.baseEvent === 'variant:add') {
        const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          // Ensure drawer is open and show loading
          showCartLoading();
          cartDrawer.show();
        }
      }
    }, true);
    
    // Listen for theme:loading:end to hide the loading overlay (only once)
    // Store handler on document to check if already added
    if (!document._cartLoadingEndHandler) {
      document._cartLoadingEndHandler = loadingEndHandler;
      document.addEventListener('theme:loading:end', loadingEndHandler);
    }
  }

  // Re-initialize when cart changes (for dynamic cart updates)
  document.addEventListener('cart:change', () => {
    setTimeout(initOTPConverter, 200);
  });
  
  // Also listen for cart drawer updates
  document.addEventListener('cart:refresh', () => {
    setTimeout(initOTPConverter, 200);
  });
  
  // Watch for dynamically added selects, buttons, and quantity selectors (for cart drawer updates)
  const observer = new MutationObserver((mutations) => {
    let shouldReinit = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          if (node.classList?.contains('subscription-plan-select') || 
              node.classList?.contains('convert-to-3jar-btn') ||
              node.classList?.contains('convert-to-subscription-btn') ||
              node.classList?.contains('custom-3jar-quantity-selector') ||
              node.classList?.contains('remove-3jar-item') ||
              node.classList?.contains('remove-cart-item') ||
              node.querySelector?.('.subscription-plan-select') ||
              node.querySelector?.('.convert-to-3jar-btn') ||
              node.querySelector?.('.convert-to-subscription-btn') ||
              node.querySelector?.('.custom-3jar-quantity-selector') ||
              node.querySelector?.('.remove-3jar-item') ||
              node.querySelector?.('.remove-cart-item')) {
            shouldReinit = true;
          }
        }
      });
    });
    if (shouldReinit) {
      setTimeout(initOTPConverter, 100);
    }
  });
  
  const otpObserverRoot = document.body || document.documentElement;
  if (otpObserverRoot) {
    observer.observe(otpObserverRoot, {
      childList: true,
      subtree: true
    });
  } else {
    document.addEventListener('DOMContentLoaded', function attachOtpObserver() {
      document.removeEventListener('DOMContentLoaded', attachOtpObserver);
      if (document.body) {
        observer.observe(document.body, { childList: true, subtree: true });
      }
    }, { once: true });
  }
  
  // Setup cart drawer refresh on open
  setupCartDrawerRefresh();
  
  // Setup add to cart intercept
  setupAddToCartIntercept();
  
  // Setup subscription product add to cart handler
  setupSubscriptionProductAddToCart();
  
  // Helper function to update progress bar with retry mechanism
  //=============================================================================
  // SECTION 7: SHIPPING PROGRESS BAR
  //=============================================================================
  // Functions for updating the shipping progress bar based on cart contents
  //=============================================================================
  
  function updateShippingProgressBarWithRetry(cartData, maxRetries = 3, retryCount = 0) {
    const cartDrawer = document.querySelector('#cart-drawer') || document.querySelector('cart-drawer');
    if (!cartDrawer) {
      if (retryCount < maxRetries) {

        setTimeout(() => updateShippingProgressBarWithRetry(cartData, maxRetries, retryCount + 1), 300);
      } else {

      }
      return;
    }
    
    // Find progress bar element (check shadow DOM first)
    let progressBar = null;
    if (cartDrawer.shadowRoot) {
      progressBar = cartDrawer.shadowRoot.querySelector('.shipping-progress-bar');
    }
    if (!progressBar) {
      progressBar = cartDrawer.querySelector('.shipping-progress-bar');
    }
    
    if (!progressBar) {
      if (retryCount < maxRetries) {

        setTimeout(() => updateShippingProgressBarWithRetry(cartData, maxRetries, retryCount + 1), 300);
      } else {

      }
      return;
    }
    
    // Element found, proceed with update
    updateShippingProgressBar(cartData);
  }
  
  // Update shipping progress bar when cart changes
  function updateShippingProgressBar(cartData) {





    
    if (!cartData) {


      return;
    }
    





    
    const cartDrawer = document.querySelector('#cart-drawer') || document.querySelector('cart-drawer');
    if (!cartDrawer) {

      return;
    }
    
    // Find progress bar element (check shadow DOM first)
    let progressBar = null;


    
    if (cartDrawer.shadowRoot) {
      progressBar = cartDrawer.shadowRoot.querySelector('.shipping-progress-bar');

    }
    if (!progressBar) {
      progressBar = cartDrawer.querySelector('.shipping-progress-bar');

    }
    
    if (!progressBar) {



      return;
    }
    

    
    const thresholdShopCentsAttr = progressBar.getAttribute('data-threshold-shop-cents');
    const thresholdAttr = progressBar.getAttribute('data-threshold');
    const shopCents = parseInt(thresholdShopCentsAttr, 10);
    const fallbackCents = parseInt(thresholdAttr, 10) || 13000;
    let rate = 1;
    if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.rate != null) {
      const r = Number(Shopify.currency.rate);
      if (Number.isFinite(r) && r > 0) rate = r;
    }
    const threshold = Number.isFinite(shopCents) && shopCents > 0
      ? Math.max(1, Math.round(shopCents * rate))
      : fallbackCents;


    
    // Calculate cart total
    let calculatedTotal = 0;
    let hasSubscription = false;
    
    if (cartData.items && Array.isArray(cartData.items)) {


      
      if (cartData.items.length === 0) {

      }
      
      cartData.items.forEach((item, index) => {


        
        const finalLinePrice = item.final_line_price;
        const linePrice = item.line_price;
        const unitPrice = item.price;
        const quantity = item.quantity;
        const calculatedPrice = finalLinePrice || linePrice || (unitPrice && quantity ? unitPrice * quantity : 0) || 0;
        const requiresShipping = item.requires_shipping !== false; // Default to true if not specified
        
        // Use requires_shipping if available, otherwise default to true (most products require shipping)
        if (requiresShipping) {
          if (calculatedPrice > 0) {
            calculatedTotal += calculatedPrice;


          } else {

          }
        } else {

        }

        
        const sellingPlan = item.selling_plan_allocation || item.sellingPlanAllocation;
        if (sellingPlan) {
          hasSubscription = true;
        }
        
        // Method 2: Check for Appstle subscriptions via line item properties
        // Appstle typically adds properties like 'frequency', 'interval', 'subscription_id', etc.
        if (item.properties && Array.isArray(item.properties)) {
          item.properties.forEach(prop => {
            const propKey = (prop.name || prop.first || '').toLowerCase();
            const propValue = (prop.value || prop.last || '').toLowerCase();
            
            // Check for common Appstle subscription property keys
            if (propKey.includes('subscription') || 
                propKey.includes('appstle') || 
                propKey.includes('frequency') || 
                propKey.includes('interval') ||
                propKey.includes('recurring')) {
              hasSubscription = true;
            }
            
            // Check property values for subscription indicators
            if (propValue.includes('subscription') || 
                propValue.includes('monthly') || 
                propValue.includes('weekly') ||
                propValue.includes('recurring')) {
              hasSubscription = true;
            }
          });
        }
        
      });
    }
    
    // Subtract cart-level discounts
    if (cartData.cart_level_discount_applications && Array.isArray(cartData.cart_level_discount_applications)) {
      const totalDiscount = cartData.cart_level_discount_applications.reduce((sum, discount) => {
        return sum + (discount.total_allocated_amount || 0);
      }, 0);
      calculatedTotal -= totalDiscount;

    }
    
    // Fallback: If calculatedTotal is 0 but cart has items, use total_price as fallback
    if (calculatedTotal === 0 && cartData.items && cartData.items.length > 0 && cartData.total_price) {

      // Only use total_price if it's reasonable (not 0)
      if (cartData.total_price > 0) {
        calculatedTotal = cartData.total_price;
      }
    }
    
    // Determine if qualifies for free shipping
    const qualifiesForFreeShipping = hasSubscription || calculatedTotal >= threshold;
    
    // Calculate remaining amount and progress
    const remainingAmount = Math.max(0, threshold - calculatedTotal);
    let progressPercentage = threshold > 0 ? (calculatedTotal / threshold) * 100 : 0;
    if (progressPercentage > 100) progressPercentage = 100;
    if (qualifiesForFreeShipping) progressPercentage = 100;
    








    
    // Update data attributes
    progressBar.setAttribute('data-total-price', calculatedTotal);
    progressBar.setAttribute('data-has-subscription', hasSubscription);
    progressBar.setAttribute('data-qualifies-free-shipping', qualifiesForFreeShipping);
    
    // Update progress fill
    const fill = progressBar.querySelector('.shipping-progress-bar__fill');
    if (fill) {
      fill.style.width = progressPercentage + '%';
    }
    
    // Update message
    const message = progressBar.querySelector('.shipping-progress-bar__message');
    if (message) {
      if (qualifiesForFreeShipping) {
        message.innerHTML = '<span class="shipping-progress-bar__success">🎉 Congrats, you have unlocked free shipping!</span>';
      } else {
        const remainingFormatted = new Intl.NumberFormat(Shopify.locale || 'en', {
          style: 'currency',
          currency: Shopify.currency?.active || 'USD'
        }).format(remainingAmount / 100);
        message.innerHTML = `<span class="shipping-progress-bar__info">You are <strong>${remainingFormatted}</strong> away from free shipping</span>`;
      }
    }
    
    // Hide shipping fee if qualified
    const feeElement = progressBar.querySelector('.shipping-progress-bar__fee');
    if (feeElement) {
      if (qualifiesForFreeShipping) {
        feeElement.style.display = 'none';
      } else {
        feeElement.style.display = 'block';
      }
    }
    // Restore grid display after quantity update
    restoreCartDrawerItemsDisplay();
  }
  
  // Helper function to fetch fresh cart and update progress bar
  function updateProgressBarFromCart() {
    setTimeout(() => {

      fetch('/cart.js')
        .then(response => {


          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text(); // Get as text first to see what we're getting
        })
        .then(text => {


          
          if (!text || text.trim() === '' || text === '{}') {

            setTimeout(() => {
              fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                  if (cart && cart.items && cart.items.length > 0) {
                    updateShippingProgressBarWithRetry(cart, 3);
                  }
                })
                .catch(() => {});
            }, 500);
            return;
          }
          
          try {
            const cart = JSON.parse(text);



            
            // Use retry mechanism to ensure element exists
            updateShippingProgressBarWithRetry(cart, 3);
          } catch (parseError) {


          }
        })
        .catch(err => {


        });
    }, 500); // Increased delay to ensure cart is fully updated
  }
  
  // Listen for cart changes to update progress bar
  document.addEventListener('cart:change', (event) => {
    const cartData = event.detail?.cart;
    
    // Check if cartData has items property (actual cart data)
    // Sometimes event.detail.cart only has sections (HTML), not actual cart JSON
    if (cartData && cartData.items && Array.isArray(cartData.items)) {
      // This is actual cart data with items

      setTimeout(() => {
        updateShippingProgressBarWithRetry(cartData, 3);
      }, 300);
    } else if (cartData && cartData.sections && !cartData.items) {
      // This is section HTML data, not cart JSON - fetch fresh cart

      updateProgressBarFromCart();
    } else {
      // No cart data in event, fetch fresh cart

      updateProgressBarFromCart();
    }
  });
  
  // Also listen for cart refresh
  document.addEventListener('cart:refresh', () => {
    updateProgressBarFromCart();
  });
})();
//=============================================================================
// END OF CUSTOM CART FUNCTIONALITY
//=============================================================================

